import {
	Box,
	Button,
	Callout,
	Card,
	Flex,
	Grid,
	Heading,
	Text,
} from '@radix-ui/themes'
import { type MetaArgs, type MetaFunction } from '@remix-run/node'
import {
	Link,
	type ShouldRevalidateFunctionArgs,
	useLoaderData,
	useOutletContext,
	useSearchParams,
} from '@remix-run/react'
import isEqual from 'lodash.isequal'
import uniqWith from 'lodash.uniqwith'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { RxPaperPlane, RxHeart, RxInfoCircled } from 'react-icons/rx'

import { z } from 'zod'
import { Pager } from '~/components/common/pager'
import { ProductGridItem } from '~/components/common/product-grid-item'
import { type GetWishlist } from '~/models/ecommerce-providers/sylius/shop/fetch/wishlist/get-wishlist'
import { type Locale } from '~/models/locale'
import { NEWSLETTER_FORM } from '~/routes/($lang).newsletter/newsletter.component'
import { type loader } from '~/routes/($lang).profile.wishlist/wishlist.server'
import {
	type Context,
	type FormEnum,
	ROOT_INPUTS,
} from '~/routes/_layout/root.component'
import { type loader as RouteLoader } from '~/routes/_layout/root.server'
import { defaultMetaI18next } from '~/utils/client/default-meta-i18next.ts'
import { generateItemFormFetcherKey } from '~/utils/client/generate-item-form-fetcher-key.ts'
import { generateItemFormId } from '~/utils/client/generate-item-form-id.ts'
import { schemaDefault } from '~/utils/form-defaults.ts'

/* -------------------------------------------------------------------------------------------------
 * I18next
 * -----------------------------------------------------------------------------------------------*/

enum I18NEXT {
	routeProfileWishlist = 'route-profile-wishlist',
	uiPager = 'ui-pager',
	uiWishlistAction = 'ui-wishlist-action',
}

/* -------------------------------------------------------------------------------------------------
 * Route loader
 * -----------------------------------------------------------------------------------------------*/

enum ROUTE_LOADER {
	root = 'root',
	current = 'routes/($lang).profile.wishlist',
}

/* -------------------------------------------------------------------------------------------------
 * Search params
 * -----------------------------------------------------------------------------------------------*/

enum SEARCH_PARAMS {
	page = 'page',
	itemsPerPage = 'itemsPerPage',
}

/* -------------------------------------------------------------------------------------------------
 * Inputs
 * -----------------------------------------------------------------------------------------------*/

enum INPUTS {
	productId = 'product-id',
	productVariantId = 'product-variant-id',
}

/* -------------------------------------------------------------------------------------------------
 * Add to wishlist form
 * -----------------------------------------------------------------------------------------------*/

const FORM_ADD_TO_WISHLIST: FormEnum = {
	generateId: ({ id }) => generateItemFormId('add-to-wishlist', id),
	action: '/profile/wishlist',
	generateFetcherKey: ({ id }) =>
		generateItemFormFetcherKey('add-to-wishlist', id),
	subaction: 'add-to-wishlist',
	redirect: '/profile/wishlist',
} as const

const addToWishlistSchema = z.object({
	...schemaDefault,
  ...{
    [INPUTS.productId]: z
      .number(),
  }
})

/* -------------------------------------------------------------------------------------------------
 * Remove from wishlist form
 * -----------------------------------------------------------------------------------------------*/

const FORM_REMOVE_FROM_WISHLIST: FormEnum = {
	generateId: ({ id }) => generateItemFormId('remove-from-wishlist', id),
	action: '/profile/wishlist',
	generateFetcherKey: ({ id }) =>
		generateItemFormFetcherKey('remove-from-wishlist', id),
	subaction: 'remove-from-wishlist',
	redirect: '/profile/wishlist',
} as const

const removeFromWishlistSchema = z.object({
	...schemaDefault,
  ...{
    [INPUTS.productId]: z
      .number(),
  }
})

/* -------------------------------------------------------------------------------------------------
 * Handle
 * -----------------------------------------------------------------------------------------------*/

const handle = { i18n: [...Object.values(I18NEXT)] }

/* -------------------------------------------------------------------------------------------------
 * Meta
 * -----------------------------------------------------------------------------------------------*/

const meta: MetaFunction<
	typeof loader,
	{
		[ROUTE_LOADER.root]: typeof RouteLoader
	}
> = ({ data, matches }: MetaArgs) => {
	const parentMeta = matches.reduce((a, b) =>
		a.meta.length > b.meta.length ? a : b,
	).meta
	const title = parentMeta.find((meta) => 'title' in meta)
	const description = parentMeta.find(
		(meta) => 'name' in meta && meta.name === 'description',
	)

	const rootData = matches.find((match) => match.id === ROUTE_LOADER.root)!
		.data as { locale: Locale }

	// @ts-ignore
	return uniqWith(
		title?.title.includes('Error')
			? [...parentMeta]
			: data
				? [
						...parentMeta.filter(
							(meta) =>
								!Object.entries(meta).find(
									([key, value]) =>
										key === 'title' ||
										(key === 'name' && value === 'description'),
								),
						),
						// @ts-ignore
						{ title: `${data.meta.title} | ${title?.title}` },
						{
							name: 'description',
							// @ts-ignore
							content: `${data.meta.desciption} | ${description?.content}`,
						},
						// Structured data -> ld+json
						{
							// @ts-ignore
							'script:ld+json': data.wishlist,
						},
						// Preload images
						// @ts-ignore
						...(
							(data.wishlist as GetWishlist).wishlistProducts?.filter(
								({ product }) => !!product.images?.[0]?.path,
							) || []
						).map(({ product }) => ({
							tagname: 'link',
							rel: 'preload',
							href: product.images[0].path!,
							as: 'image',
						})),
						// I18next
						...defaultMetaI18next<typeof I18NEXT>({
							I18NEXT,
							locale: rootData.locale,
						}),
					]
				: [
						...parentMeta.filter(
							(meta) =>
								!Object.entries(meta).find(
									([key, value]) =>
										key === 'title' ||
										(key === 'name' && value === 'description'),
								),
						),
						// @ts-ignore
						{ title: `Error | ${title?.title}` },
						{
							name: 'description',
							// @ts-ignore
							content: `Error | ${description?.content}`,
						},
					],
		(a, b) => isEqual(a, b),
	)
}

/* -------------------------------------------------------------------------------------------------
 * Should revalidate
 * -----------------------------------------------------------------------------------------------*/

const shouldRevalidate = ({
	formData,
	defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs) => {
	if (formData?.get(ROOT_INPUTS.subaction) === NEWSLETTER_FORM.subaction) {
		return false
	}

	return defaultShouldRevalidate
}

/* -------------------------------------------------------------------------------------------------
 * Component
 * -----------------------------------------------------------------------------------------------*/

const Component: React.FC = () => {
	const { wishlist, total, itemsPerPage, page } = useLoaderData<typeof loader>()
	const { locale } = useOutletContext<Context>()
	const [searchParams] = useSearchParams()
	const { t } = useTranslation(I18NEXT.routeProfileWishlist)

	return (wishlist as GetWishlist).wishlistProducts?.length ? (
		<Box flexGrow="1">
			<Grid
				asChild
				columns={{ initial: '1', sm: '2', md: '2', lg: '2' }}
				gap={{ initial: '3', xs: '5', sm: '4', md: '5' }}
			>
				<ul>
					{(wishlist as GetWishlist).wishlistProducts!.map(({ product }) => (
						<ProductGridItem
							key={product.id}
							product={product}
							inWishlist={true}
							className="app-ProductAssociations mx-auto !max-w-lg md:!max-w-xl"
						/>
					))}
				</ul>
			</Grid>
			<Box asChild mt="7">
				<Pager
					total={total}
					itemsPerPage={itemsPerPage}
					page={page}
					preventScrollReset
				/>
			</Box>
		</Box>
	) : (
		<Flex align="center" justify="center" width="100%" height="100%">
			<Card size="5">
				<Flex direction="column" align="center" gapY="4">
					<Text color="green">
						<RxHeart size="2.5em" />
					</Text>
					<Heading as="h3" color="gray">
						{t('noItems.common.title')}
					</Heading>
					{!(wishlist as GetWishlist).wishlistProducts?.length &&
						(searchParams.has(SEARCH_PARAMS.page) ||
							searchParams.has(SEARCH_PARAMS.itemsPerPage)) && (
							<Callout.Root color="blue">
								<Callout.Icon>
									<RxInfoCircled />
								</Callout.Icon>
								<Callout.Text>
									{t('noItems.common.wrongSearchParams')}
								</Callout.Text>
							</Callout.Root>
						)}
					<Link to={`/${locale}/products`} prefetch="viewport">
						<Button size="3" mt="3">
							<RxPaperPlane size="1.2em" />
							{t('noItems.button.products')}
						</Button>
					</Link>
				</Flex>
			</Card>
		</Flex>
	)
}
Component.displayName = 'Component'

/* -----------------------------------------------------------------------------------------------*/

export {
	Component,
	shouldRevalidate,
	handle,
	meta,
	I18NEXT as PROFILE_WISHLIST_I18NEXT,
	ROUTE_LOADER as PROFILE_WISHLIST_ROUTE_LOADER,
	SEARCH_PARAMS as PROFILE_WISHLIST_SEARCH_PARAMS,
	INPUTS as PROFILE_WISHLIST_INPUTS,
	FORM_ADD_TO_WISHLIST as PROFILE_WISHLIST_FORM_ADD_TO_WISHLIST,
	FORM_REMOVE_FROM_WISHLIST as PROFILE_WISHLIST_FORM_REMOVE_FROM_WISHLIST,
	addToWishlistSchema as profileWishlistAddToWishlistSchema,
	removeFromWishlistSchema as profileWishlistRemoveFromWishlistSchema,
}
